<template>
    <div :key="`render-` + renderKey">
        <app-wrapper :loan="loan">
            <app-header :loan="loan" medium-max-width="max-w-1000">
                <template #navigation>
                    <div class="max-w-200">
                        <h5 class="font-zen-dots text-white">
                            <AppLogo />
                        </h5>
                    </div>
                </template>
                <template #top>
                    <div class="w-full flex items-center">
                        <div class="flex-auto">
                            Your desired loan amount
                            <h2 class="mb-0 mt-2 md:mt-4 md:text-6xl">{{ loan.amount | money(0) }}</h2>
                        </div>
                        <div class="flex-initial">
                            <i class="fa fa-check-circle text-success text-7xl"/>
                        </div>
                    </div>
                </template>
            </app-header>
            <app-body medium-max-width="max-w-500" footer>
                <template #title>
                    <template v-if="sortedAccounts.length || loadingAccountNumbers">
                        You're almost there!
                    </template>
                    <template v-else-if="hasDisconnectedBanks">
                        Bank Connection Lost
                    </template>
                    <template v-else>
                        Just few more things
                    </template>
                </template>

                <template v-if="loadingAccountChange">
                    <div class="flex flex-col space-y-2.5 mt-7">
                        <div class="flex cursor-pointer shadow items-center border py-4 px-5 rounded-lg bg-gray-50 border-gray-300">
                            <h6><i class="fa fa-circle-notch fa-spin"/></h6>
                            <div class="ml-4">
                                <template>Just a moment!</template>
                                <template>Let's go back a step!</template>
                            </div>
                        </div>
                    </div>
                </template>

                <template #text>
                    <template v-if="loadingAccountNumbers">Just a moment!</template>
                    <template v-else-if="hasDisconnectedBanks && ! sortedAccounts.length">Your bank connection was lost. Please reconnect your bank to continue.</template>
                    <template v-else-if="! sortedAccounts.length">Sorry but it's taking longer than usual to get the information we need, we'll be in touch with next steps.</template>
                    <template v-else>Please select the account through which you receive the majority of your income.</template>
                </template>

                <template #main>
                    <div v-if="hasDisconnectedBanks && !sortedAccounts.length" class="mt-7">
                        <button @click="reconnectPlaid"
                                class="w-full button shadow-action font-semibold text-lg bg-action rounded text-white py-3 px-6">
                            Reconnect Your Bank
                        </button>
                    </div>

                    <div v-if="sortedAccounts.length || loadingAccountNumbers" class="flex flex-col space-y-2.5 mt-7">
                        <div v-if="loadingAccountNumbers" class="flex cursor-pointer shadow items-center border py-4 px-5 rounded-lg bg-gray-50 border-gray-300">
                            <div class="w-2/5 pr-5">
                                <h6><i class="fa fa-circle-notch fa-spin"/></h6>
                            </div>
                            <div class="w-3/5 text-right">
                                <p>Still loading...</p>
                            </div>
                        </div>

                        <div v-else v-for="(account, index) in sortedAccounts"
                             @click="selectAccount(account)"
                             :key="`sorted-` + account.id"
                             class="account-info cursor-pointer shadow items-center border py-4 px-5 rounded-lg relative"
                             :class="{
                                    'border-primary bg-indigo-100': selected && selected.id === account.id,
                                    'bg-gray-50 border-gray-300': ! selected || selected.id !== account.id
                                }"
                            >
                                <div v-if="account?.income_description" class="absolute" style="left:-15px;top:-11px">
                                    <i class="fa fa-check-circle text-success text-3xl"></i>
                                </div>

                                <div class="w-full flex items-center justify-center mb-1">
                                    <div class="border-t border-gray-400 flex-grow"></div>
                                    <div class="mx-4 text-gray-500 text-sm">{{ account?.banking_user?.name }}</div>
                                    <div class="border-t border-gray-400 flex-grow"></div>
                                </div>
                                <div class="flex">
                                    <div class="w-2/5 pr-5">
                                        <p class="mb-3">Account</p>
                                        <p class="mb-3" v-if="account.subtype">Account type</p>
                                        <p class="mb-3" v-else>Account number</p>
                                        <p v-if="account?.edge_exists && account?.income_description">Income found:</p>
                                    </div>
                                    <div class="w-3/5 text-right" :class="{ 'font-semibold': selected && selected.account_id === account.account_id }">
                                        <p class="mb-3 truncate" v-text="account.official_name || account.name"></p>
                                        <p class="mb-3" v-if="account.subtype">{{ formatSubType(account.subtype) }} ({{ account.mask }})</p>
                                        <p class="mb-3" v-else>{{ account.mask }}</p>
                                        <p v-if="account?.edge_exists && account?.income_description" class="mb-3 text-sm text-success">
                                            <i class="fas fa-check"></i> {{ account?.income_description }}
                                        </p>
                                        <p v-else-if="account?.edge_exists && !account?.income_description" class="mb-3 text-sm text-red-600">
                                            <i class="fas fa-times"></i> No income found
                                        </p>
                                    </div>
                                </div>
                                <div class="w-full border-t border-slate-500 mt-4 pt-4" v-if="selected && selected.id === account.id && manualEntryRequiredInstitutionCodes.includes(account.banking_user.external_institution_code)">
                                    <div class="mb-4">Please confirm for this account:</div>

                                    <label for="manual_routing_number" class="text-xs font-medium mb-1 flex justify-between">
                                        Routing Number:
                                        <span v-if="manualErrors.hasOwnProperty('manual_routing_number')" class="font-semibold text-red-500">
                                            {{ manualErrors.manual_routing_number.join(', ') }}
                                        </span>
                                    </label>
                                    <input type="text"
                                        id="manual_routing_number"
                                        name="manual_routing_number"
                                        class="input-field w-full"
                                        placeholder="Routing Number"
                                        v-model="manual_routing_number[account.id]"/>

                                    <label for="manual_account_number" class="text-xs font-medium block mt-2 mb-1">Account Number:</label>
                                    <input type="text"
                                        id="manual_account_number"
                                        name="manual_account_number"
                                        class="input-field w-full"
                                        placeholder="Account Number"
                                        v-model="manual_account_number[account.id]"/>
                                </div>
                        </div>
                    </div>

                    <div class="w-full mt-7">
                      <button v-if="! loadingAccountNumbers && sortedAccounts.length > 0"
                              id="confirm"
                              @click.prevent="onConfirmClick"
                              class="w-full button shadow-action font-semibold text-lg bg-action rounded text-white py-3 px-6"
                              :class="{
                                '': readyToSubmit,
                                'cursor-not-allowed opacity-50': ! readyToSubmit
                            }">
                        <template v-if="! loading">
                          Confirm
                        </template>
                        <template v-else>
                          <i class="fa fa-circle-notch fa-spin"/>
                        </template>
                      </button>
                    </div>

                    <div v-if="loan.banking_vendor?.name === 'mx'" class="flex items-center bg-gray-50 shadow rounded-lg mt-7 md:h-full">
                        <div class="flex-auto py-6 px-6">
                            <svg class="h-6" viewBox="0 0 77 35" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <g id="logo">
                                    <g id="MX LOGO White">
                                        <path id="Fill 1" fill-rule="evenodd" clip-rule="evenodd" d="M40.2362 33.4237L44.6649 27.7595C44.7018 27.7223 44.7018 27.6483 44.7018 27.6115L40.7944 0.80832C40.7944 0.697051 40.6831 0.623047 40.5713 0.623047H35.4731C35.3987 0.623047 35.3238 0.660311 35.2868 0.734316L23.1177 25.3898C23.0433 25.5383 22.8196 25.5383 22.7452 25.3898L10.4643 0.734316C10.4274 0.660311 10.353 0.623047 10.2786 0.623047H5.14294C5.03109 0.623047 4.9567 0.697051 4.91977 0.80832L0.00728769 34.4603C-0.0301719 34.5716 0.0816792 34.6823 0.230462 34.6823H6.18495C6.29628 34.6823 6.37067 34.6083 6.40813 34.4971L9.38537 12.9141C9.4223 12.692 9.68294 12.6548 9.79479 12.8401L20.6243 34.5716C20.6612 34.6456 20.7356 34.6823 20.81 34.6823H25.0155C25.0899 34.6823 25.1643 34.6456 25.2012 34.5716L35.8825 13.1361C35.9938 12.9513 36.2544 12.9881 36.2914 13.2101L39.1942 34.127C39.2317 34.3123 39.4549 34.3858 39.5662 34.2383L40.2362 33.4237Z" fill="#28282A"/>
                                        <g id="Group 5">
                                            <mask id="mask0_4236_517" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="42" y="0" width="35" height="35">
                                                <path id="Clip 4" fill-rule="evenodd" clip-rule="evenodd" d="M42.6094 34.7194H76.7338V0.623047H42.6094V34.7194H42.6094Z" fill="white"/>
                                            </mask>
                                            <g mask="url(#mask0_4236_517)">
                                                <path id="Fill 3" fill-rule="evenodd" clip-rule="evenodd" d="M69.0018 0.623047C68.9274 0.623047 68.89 0.660311 68.8161 0.697051L59.8469 12.396C59.7725 12.5068 59.5868 12.5068 59.5119 12.396L50.5063 0.697051C50.4688 0.660311 50.3944 0.623047 50.32 0.623047H42.8397C42.654 0.623047 42.5796 0.84506 42.654 0.956329L55.5301 17.5417C55.6045 17.6157 55.6045 17.727 55.5301 17.801L42.654 34.3863C42.5422 34.5343 42.654 34.7196 42.8397 34.7196H50.32C50.3944 34.7196 50.4319 34.6823 50.5063 34.6456L59.5119 22.9466C59.5868 22.8359 59.7725 22.8359 59.8469 22.9466L68.853 34.6456C68.89 34.6823 68.9644 34.7196 69.0393 34.7196H76.5191C76.7053 34.7196 76.7797 34.4976 76.7053 34.3863L63.8292 17.801C63.7543 17.727 63.7543 17.6157 63.8292 17.5417L76.7053 0.956329C76.7428 0.80832 76.6684 0.623047 76.4821 0.623047H69.0018Z" fill="#28282A"/>
                                            </g>
                                        </g>
                                    </g>
                                </g>
                            </svg>
                        </div>
                        <div class="flex-auto">
                            <p class="pr-4 md:pr-6">Your information is secured by MX's bank level security standards.</p>
                        </div>
                    </div>
                    <div v-else-if="loan.banking_vendor?.name === 'plaid'" class="flex items-center bg-gray-50 shadow rounded-lg mt-7 md:h-full">
                        <div class="flex-auto py-6 px-6">
                            <svg class="h-8" viewBox="0 0 126 48" xmlns="http://www.w3.org/2000/svg">
                                <g fill="#111" fill-rule="evenodd">
                                    <path d="M66.248 16.268c-1.057-.889-2.861-1.333-5.413-1.333h-5.756v17.788h4.304v-5.575h1.928c2.34 0 4.056-.515 5.148-1.546 1.23-1.155 1.849-2.693 1.849-4.613 0-1.991-.687-3.565-2.06-4.721m-5.044 6.855h-1.821V18.96h1.636c1.99 0 2.985.698 2.985 2.094 0 1.378-.934 2.068-2.8 2.068M75.673 14.934h-4.488v17.788h9.69v-4.026h-5.202zM89.668 14.934l-7.05 17.788h4.832l.924-2.586H94.5l.845 2.586h4.886l-7-17.788zm-.053 11.601l1.849-6.08 1.82 6.08z"></path>
                                    <path
                                        d="M102.473 32.722h4.489V14.934h-4.489zM124.39 18.268a7.376 7.376 0 00-2.14-2.053c-1.355-.854-3.204-1.28-5.545-1.28h-5.914v17.787h6.918c2.5 0 4.506-.817 6.02-2.453 1.514-1.635 2.27-3.805 2.27-6.508 0-2.15-.537-3.981-1.61-5.493m-7.182 10.427h-1.927v-9.734h1.954c1.373 0 2.428.43 3.168 1.287.74.857 1.11 2.073 1.11 3.647 0 3.2-1.435 4.8-4.305 4.8M18.637 0L4.09 3.81.081 18.439l5.014 5.148L0 28.65l3.773 14.693 14.484 4.047 5.096-5.064 5.014 5.147 14.547-3.81 4.008-14.63-5.013-5.146 5.095-5.063L43.231 4.13 28.745.083l-5.094 5.063zM9.71 6.624l7.663-2.008 3.351 3.44-4.887 4.856zm16.822 1.478l3.405-3.383 7.63 2.132-6.227 6.187zM4.672 17.238l2.111-7.705 6.125 6.288-4.886 4.856zm29.547-1.243l6.227-6.189 1.986 7.74-3.404 3.384zm-15.502-.127l4.887-4.856 4.807 4.936-4.886 4.856zm-7.814 7.765l4.886-4.856 4.81 4.936-4.888 4.856zm15.503.127l4.886-4.856L36.1 23.84l-4.887 4.856zM4.57 29.927l3.406-3.385 4.807 4.937-6.225 6.186zm14.021 1.598l4.887-4.856 4.808 4.936-4.886 4.856zm15.502.128l4.887-4.856 3.351 3.439-2.11 7.705zm-24.656 8.97l6.226-6.189 4.81 4.936-3.406 3.385zm16.843-1.206l4.886-4.856 6.126 6.289-7.662 2.007z"></path>
                                </g>
                            </svg>
                        </div>
                        <div class="flex-auto">
                            <p class="pr-4 md:pr-6">Your information is secured by Plaid's bank level security standards.</p>
                        </div>
                    </div>

                    <confirm
                        :account="selected"
                        :open="openConfirm"
                        :confirm-message="getConfirmMessage()"
                        :account-number="confirmMaskedAccountNumber"
                        @confirmed="submit"
                        @close="closeConfirmModal()"/>
                </template>

                <template #footer>
                    <button @click="reconnectPlaid" type="submit" class="underline text-black">Don't see your account? Click here to re-connect to Plaid</button>
                </template>

            </app-body>
        </app-wrapper>
    </div>
</template>

<script>
import { forIn } from 'lodash';
import Confirm from '../agreed/Confirm';
import AppLogo from './../../../components/AppLogo';
import { analytics } from './../../../analytics';
import { normalize } from '../../../utils';

export default {
    mixins: [require('../../../mixins/InteractsWithGtag').default],
    mixins: [require('../../../mixins/PageViewAnalytics').default],

    props: ['accounts', 'loan', 'manualEntryRequiredInstitutionCodes', 'hasDisconnectedBanks'],

    components: {
        Confirm,
        AppLogo
    },

    watch: {
        accounts: {
            handler: function (val) {
                this.sortedAccounts = _.sortBy(val, 'subtype');
            },
            deep: true,
            immediate: true
        }
    },

    computed: {
        /**
         * @returns {boolean}
         */
        readyToSubmit() {
            let ready = true;

            this.accounts.forEach(account => {
                if (
                    this.selected
                    && this.selected.id === account.id
                    && account.banking_user
                    && this.manualEntryRequiredInstitutionCodes.includes(account.banking_user.external_institution_code)
                    && (!this.manual_routing_number[account.id] || !this.manual_account_number[account.id])
                ) {
                    console.log('not ready');

                    ready = false;
                }
            });

            return ready;
        },

        /**
         * @returns {Boolean}
         */
        loadingAccountNumbers() {
            return this.loan.sub_status === 'gathering_account_numbers';
        },

        /**
         * @returns {Array}
         */
        unsignedDocuments() {
            return this.loan.documents.filter(document => document.status !== 'signed');
        },

        /**
         * @returns {Boolean}
         */
        allDocumentsSigned() {
            return !this.unsignedDocuments.length;
        },

        confirmMaskedAccountNumber() {
            const hasProp = this.manual_account_number.hasOwnProperty(this.selected?.id || '');
            if (hasProp && this.manual_account_number[this.selected.id]) {
                return '*******' + this.manual_account_number[this.selected.id].slice(-4);
            }

            return this.selected?.mask || '';
        },

        /**
         * Account identifier for the confirm message, or null when there is none to show.
         * A tokenized account_number is a Plaid token rather than a real number, so the
         * mask is used instead - and an all-asterisk mask carries no digits at all. The full
         * number is never sent to the browser, so only its last four are shown.
         *
         * @returns {String|null}
         */
        confirmAccountLabel() {
            if (!this.selected?.is_tokenized_account_number) {
                const lastFour = this.selected?.account_number_last_four;

                return lastFour ? '******' + lastFour : null;
            }

            const mask = this.selected?.mask ?? '';

            return /\d{4}$/.test(mask) ? mask : null;
        }
    },

    created() {
        // Prefer an account with a banking_user (Plaid-linked). createLoan / stubs may
        // leave an earlier depository row without a banking_user relation.
        this.selected = this.accounts.find((account) => account.banking_user) || this.accounts[0];
    },

    async mounted() {
        if (this.accounts?.length) {
            const incomePromises = this.accounts.map(async (account) => {
                const edgeExists = await this.checkIfEdgeExistsForAccount(account.id);

                if (edgeExists === true) {
                    const data = await this.getIncomeForBankAccount(account.id);
                    this.$set(account, 'income_description', data?.data?.income_description || null);
                    this.$set(account, 'edge_score', data?.data?.score || null);
                }

                this.$set(account, 'edge_exists', edgeExists);
                return edgeExists;
            });

            await Promise.all(incomePromises);
            this.renderKey++;
        }

        if (this.loadingAccountNumbers) {
            setTimeout(() => {
                window.location = window.location;
            }, 5000);
        }

    },

    data() {
        return {
            gTagPageName: 'SET_DEPOSIT',
            loading: false,
            loadingAccountChange: false,
            selected: null,
            openConfirm: false,
            manual_routing_number: {},
            manual_account_number: {},
            manualErrors: [],
            edgeData: [],
            renderKey: 0,
            sortedAccounts: [..._.sortBy(this.accounts, 'subtype')]
        }
    },

    methods: {

        /**
         * Safe, non-PII identifiers for the selected account - never the account row or any
         * typed number itself. Shared by every setDeposit* analytics call that needs to know
         * which account is selected.
         *
         * @returns {Object}
         */
        depositAnalyticsParams() {
            const mask = this.selected?.mask ?? '';
            const accountId = this.selected?.id;

            return {
                user_id: this.loan.user.id,
                loan_id: this.loan.id,
                loan_status: this.loan?.status ?? null,
                page_name: this.gTagPageName,
                selected_account_id: this.selected?.id ?? null,
                selected_account_subtype: this.selected?.subtype ?? null,
                selected_account_last_four: /\d{4}$/.test(mask) ? mask.slice(-4) : null,
                has_manual_numbers: !!(accountId && (this.manual_account_number[accountId] || this.manual_routing_number[accountId])),
            };
        },

        trackConfirmClick() {
            analytics.marketPlaceSignUpEvents.setDepositConfirmClicked({
                user_id: this.loan.user.id,
                loan_id: this.loan.id,
                loan_status: this.loan?.status ?? null,
                page_name: this.gTagPageName,
            });
        },

        onConfirmClick() {
            if (!this.readyToSubmit) {
                return;
            }

            this.openConfirm = true;
            this.trackConfirmClick();
        },

        /**
         * @param account
         */
        selectAccount(account) {

            if(this.selected && this.selected.id === account.id) {
                return;
            }

            if (
                this.selected
                && this.selected.id !== account.id
                && this.selected.banking_user
                && this.manualEntryRequiredInstitutionCodes.includes(this.selected.banking_user.external_institution_code)
            ) {
                this.manual_account_number[this.selected.id] = null;
                this.manual_routing_number[this.selected.id] = null;
            }

            this.selected = account;

            analytics.marketPlaceSignUpEvents.setDepositSelectAccountClick(this.depositAnalyticsParams());
        },

        /**
         * @param {String} subType
         * @returns {String}
         */
        formatSubType(subType) {
            switch (subType) {
                case 'cd':
                    return 'CD';
                default:
                    return !subType ? '' : this.$options.filters.ucfirst(subType);
            }
        },

        /**
         * @returns {void}
         */
        goToDocumentSigning() {
            window.location = `/account/loans/${this.loan.id}/documents`;
        },

        /**
         * @returns {void}
         */
        goToEmailVerification() {
            window.location = "/account/verify-email";
        },

        closeConfirmModal() {
            this.openConfirm = false;

            analytics.marketPlaceSignUpEvents.setDepositCloseConfirmModal(this.depositAnalyticsParams());
        },

        /**
         * @returns {void}
         */
        submit() {
            this.openConfirm = false;
            this.loading = true;

            analytics.marketPlaceSignUpEvents.setDepositConfirmConfirmClicked(this.depositAnalyticsParams());

            this.$http.post(`/signup/welcome/${this.loan.id}`, {
                banking_account_id: this.selected.id,
                income_found: !!this.selected?.income_description,
                manual_routing_number: this.selected?.id ? this.manual_routing_number[this.selected.id] : null,
                manual_account_number: this.selected?.id ? this.manual_account_number[this.selected.id] : null,
            }).then(({data}) => {
                analytics.marketPlaceSignUpEvents.setDepositSelectAccount({
                    ...this.depositAnalyticsParams(),
                    event_category: 'onboarding',
                    event_label: 'loan-approved',
                });
                this.loading = false;

                window.location.reload();
            }, error => {
                this.loading = false;

                this.manualErrors = error.response.data.errors;

                analytics.marketPlaceSignUpEvents.setDepositSelectAccountError({
                    ...this.depositAnalyticsParams(),
                    event_category: 'onboarding',
                    event_label: 'loan-approved',
                    error_fields: Object.keys(error.response?.data?.errors ?? {}),
                });

            });
        },

        getConfirmMessage() {
            const bankName = this.selected?.name || this.selected?.official_name || '';

            let accountPhrase = 'your selected account';

            if (this.confirmAccountLabel) {
                accountPhrase = `account <strong>${this.confirmAccountLabel}</strong>`;
            } else if (bankName) {
                // The message is rendered with v-html, so the bank name has to be escaped.
                accountPhrase = `your selected account at <strong>${_.escape(bankName)}</strong>`;
            }

            if (this.selected?.income_description) {
                return `Please confirm that ${accountPhrase} is where you receive majority of your income`;
            }

            return `Our system is initially unable to verify income in this account. Please confirm that ${accountPhrase} is where you receive the majority of your income.`;
        },

        reconnectPlaid() {
            if (!confirm('Are you sure you want to re-connect your bank accounts?')){
                return;
            }

            analytics.marketPlaceSignUpEvents.setDepositReconnectToPlaid({
                user_id: this.loan.user.id,
                loan_id: this.loan.id,
                loan_status: this.loan?.status ?? null,
                page_name: this.gTagPageName,
            });

            this.loadingAccountChange = true;

            this.$http.post(`/signup/reconnect-bank-accounts/${this.loan.id}`).then(() => {
                window.location.reload();
            }, error => {
                bugsnag.notify(error);
                window.location.reload();
            });
        },

        async checkIfEdgeExistsForAccount(bankingAccountId){
            const response = await this.$http.get(`/account/exists-in-edge/${bankingAccountId}`);
            return response.data?.exists;
        },

        async getIncomeForBankAccount(bankingAccountId){
            const response = await this.$http.get(`/account/income-for-bank-account/${bankingAccountId}`);
            return response.data;
        }
    }
}
</script>


<style scoped>
.account-info:not(:last-child) {
    margin-bottom: 10px;
}
</style>